'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Select, Button, Empty } from 'antd';
import { StarFilled } from '@ant-design/icons';
import ReviewSummaryComponent from './ReviewSummary';
import ReviewCard from './ReviewCard';
import AIReviewSummary from './AIReviewSummary';
import {
  fetchReviewSummary,
  fetchEntityReviews,
  fetchAIReviewSummary,
} from '../utils/reviewerApi';
import type { ReviewSummary, Review } from '../types';

interface ReviewsSectionProps {
  entityId: number;
  entityName: string;
  onWriteReview?: () => void;
}

/** Maps the UI sort labels to API sort parameter values */
const SORT_OPTIONS: { label: string; value: string }[] = [
  { label: 'Most Recent', value: 'recent' },
  { label: 'Most Helpful', value: 'helpful' },
  { label: 'Highest Rated', value: 'highest' },
  { label: 'Lowest Rated', value: 'lowest' },
];

const PER_PAGE = 6; // Show 6 at a time (3 rows of 2 columns)

const ReviewsSection: React.FC<ReviewsSectionProps> = ({
  entityId,
  entityName,
  onWriteReview,
}) => {
  // ── State ───────────────────────────────────────────────────
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [sort, setSort] = useState('recent');
  const [page, setPage] = useState(1);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [aiSummary, setAiSummary] = useState<string | null>(null);
  const [loadingAiSummary, setLoadingAiSummary] = useState(true);

  const hasMore = reviews.length < totalReviews;

  // ── Fallback write-review handler ───────────────────────────
  const handleWriteReview = useCallback(() => {
    if (onWriteReview) {
      onWriteReview();
    }
  }, [onWriteReview]);

  // ── Fetch summary on mount ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const loadSummary = async () => {
      setLoadingSummary(true);
      try {
        const data = await fetchReviewSummary(entityId);
        if (!cancelled) {
          setSummary(data);
        }
      } catch (err) {
        console.error('[ReviewsSection] Failed to load summary:', err);
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    };
    loadSummary();
    return () => {
      cancelled = true;
    };
  }, [entityId]);

  // ── Fetch AI review summary in parallel ────────────────────
  useEffect(() => {
    let cancelled = false;
    const loadAiSummary = async () => {
      setLoadingAiSummary(true);
      try {
        const data = await fetchAIReviewSummary(entityId);
        if (!cancelled) {
          setAiSummary(data?.ai_summary ?? null);
        }
      } catch {
        if (!cancelled) setAiSummary(null);
      } finally {
        if (!cancelled) setLoadingAiSummary(false);
      }
    };
    loadAiSummary();
    return () => { cancelled = true; };
  }, [entityId]);

  // ── Dispatch review summary event for teaser component ──────
  useEffect(() => {
    if (!loadingSummary && summary && summary.total_reviews > 0) {
      window.dispatchEvent(
        new CustomEvent('review-summary-loaded', {
          detail: {
            average_rating: summary.average_rating,
            total_reviews: summary.total_reviews,
          },
        })
      );
    }
  }, [loadingSummary, summary]);

  // ── Fetch reviews (resets on sort change) ───────────────────
  useEffect(() => {
    let cancelled = false;
    const loadReviews = async () => {
      setLoadingReviews(true);
      setPage(1);
      try {
        const data = await fetchEntityReviews(entityId, 1, PER_PAGE, sort);
        if (!cancelled) {
          setReviews(data.reviews ?? []);
          setTotalReviews(data.total ?? 0);
        }
      } catch (err) {
        console.error('[ReviewsSection] Failed to load reviews:', err);
        if (!cancelled) {
          setReviews([]);
          setTotalReviews(0);
        }
      } finally {
        if (!cancelled) setLoadingReviews(false);
      }
    };
    loadReviews();
    return () => {
      cancelled = true;
    };
  }, [entityId, sort]);

  // ── Load more handler ──────────────────────────────────────
  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const data = await fetchEntityReviews(
        entityId,
        nextPage,
        PER_PAGE,
        sort,
      );
      setReviews((prev) => [...prev, ...(data.reviews ?? [])]);
      setTotalReviews(data.total ?? totalReviews);
      setPage(nextPage);
    } catch (err) {
      console.error('[ReviewsSection] Failed to load more reviews:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [entityId, sort, page, loadingMore, hasMore, totalReviews]);

  // ── Sort change handler ────────────────────────────────────
  const handleSortChange = useCallback((value: string) => {
    setSort(value);
  }, []);

  // ── Helpful toggle callback ────────────────────────────────
  const handleHelpfulToggle = useCallback(
    (reviewId: string, newCount: number, isHelpful: boolean) => {
      setReviews((prev) =>
        prev.map((r) =>
          r.id === reviewId
            ? { ...r, helpful_count: newCount, is_helpful_by_me: isHelpful }
            : r,
        ),
      );
    },
    [],
  );

  // ── Hide entirely when loading or no reviews — CTAs live in sidebar ──
  if (loadingSummary || loadingReviews) {
    return null;
  }

  if (!summary || !summary.total_reviews) {
    return null;
  }

  // ── Render (Airbnb-style layout) ────────────────────────────
  return (
    <section id="reviews-section" className="bg-white rounded-lg shadow-sm border p-6">
      {/* ── Airbnb-style header: ★ 4.81 · 207 reviews ──────── */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <StarFilled style={{ color: '#fadb14', fontSize: 22 }} />
          <span>{(summary.average_rating ?? 0).toFixed(2)}</span>
          <span className="text-gray-400 font-normal mx-1">·</span>
          <span>
            {summary.total_reviews} {summary.total_reviews === 1 ? 'review' : 'reviews'}
          </span>
        </h2>

        {/* Sort control */}
        {reviews.length > 1 && (
          <Select
            value={sort}
            onChange={handleSortChange}
            options={SORT_OPTIONS}
            size="small"
            className="!min-w-[150px]"
            popupMatchSelectWidth={false}
          />
        )}
      </div>

      {/* ── Summary: bar chart + sub-rating icons grid ───────── */}
      <ReviewSummaryComponent
        summary={summary}
        onWriteReview={handleWriteReview}
      />

      {/* ── AI-generated summary + sentiment bar ────────────── */}
      <AIReviewSummary
        aiSummary={aiSummary}
        loading={loadingAiSummary}
        ratingDistribution={summary?.rating_distribution ?? {}}
        totalReviews={summary?.total_reviews ?? 0}
      />

      {/* ── Divider ────────────────────────────────────────── */}
      <hr className="my-8 border-gray-200" />

      {/* ── Reviews 2-column grid (Airbnb-style) ────────────── */}
      {reviews.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0">
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onHelpfulToggle={handleHelpfulToggle}
              />
            ))}
          </div>

          {/* "Show all X reviews" button */}
          {hasMore && (
            <div className="flex justify-start pt-6">
              <Button
                onClick={handleLoadMore}
                loading={loadingMore}
                size="large"
                className="!rounded-lg !font-semibold !border-gray-900 !text-gray-900 hover:!bg-gray-100 !px-6"
              >
                Show all {totalReviews} reviews
              </Button>
            </div>
          )}
        </>
      ) : (
        summary.total_reviews > 0 && (
          <div className="mt-6">
            <Empty
              description="No reviews match the current filter"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </div>
        )
      )}
    </section>
  );
};

export default ReviewsSection;
